import { createApp } from 'vue';
import './plugins/axios';

// CodeEffects Rule Editor - loaded globally so window.$rule is available before any component mounts
import 'codeeffects';
import 'codeeffects/codeeffects.common.css';
import 'codeeffects/codeeffects.light.css';
import './assets/codeeffects/codeeffects.common.e360.custom.css';
import './assets/codeeffects/codeeffects.gray.e360.custom.css';

import { useDisplay } from 'vuetify';
import vuetify from './plugins/vuetify';
// Vuetify 3 -> 4 visual compatibility (MD2 type scale + legacy elevation)
import './assets/vuetify4-compat.css';
// import './Core.UI.Domain/Styles/variables.scss';
// import './Core.UI.Domain/Styles/settings.scss';

import ToastPlugin from './plugins/toasts';
import ability from './plugins/abilities';
import { abilitiesPlugin } from '@casl/vue';

import './plugins/weather-icon-mapper';

import E360App from './E360App.vue';
import router from './router';
import { store } from './store';
import { globalConfig } from './configuration/config';
import { initializeInputTrimming } from './Core.Patterns/Utility';
import { datadogRum } from '@datadog/browser-rum';
import { version } from '../package.json';

import VueTheMask from 'vue-the-mask';

import {
  registerChunkReload,
  isChunkLoadError,
  reloadForNewChunks,
} from './utils/chunk-reload';

// Recover from stale-deploy chunk load failures (component-level lazy imports).
registerChunkReload();

document.title = globalConfig.get()?.APP_BRANDING
  ? globalConfig.get().APP_BRANDING
  : 'E360';

const isProduction = import.meta.env.PROD;
const env = import.meta.env.MODE;

import HighchartsVue from 'highcharts-vue';

const app = createApp(E360App);

const isSSOUser = (userId) => {
  // if no user is passed in, we're going to use the current, logged-in user
  userId = userId ?? store.getters['session/jwtSubject'] ?? 'unknown';
  // auth0 users start with auth0;
  // SSO users start with various other strings based on connection type (okta|, oidc|, etc)
  return !userId.startsWith('auth0|');
};

app.provide('isSSOUser', isSSOUser);
app.provide('BUILD_TAG', globalConfig.get().BUILD_TAG);

app.use(vuetify);
app.use(router);
app.use(store);
app.use(HighchartsVue);
app.use(VueTheMask);

app.use(abilitiesPlugin, ability, {
  useGlobalProperties: true,
});
app.directive('blur', {
  mounted(el) {
    el.onfocus = (ev) => ev.target.blur();
  },
});
app.mixin({
  computed: {
    smallBreakpoint() {
      const { mobile } = useDisplay();
      return mobile.value;
    },
  },
});
app.use(ToastPlugin);

if (isProduction && globalConfig.get().DYNO_DD_APPLICATION_ID) {
  datadogRum.init({
    applicationId: globalConfig.get().DYNO_DD_APPLICATION_ID,
    clientToken: globalConfig.get().DYNO_DD_CLIENT_ID,
    site: 'us3.datadoghq.com',
    service: 'e360-web-app',
    env,
    version,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
  });

  datadogRum.startSessionReplayRecording();
}

const ignoredRoutes = ['/reports'];
app.config.errorHandler = (err, vm) => {
  const options = {
    title: 'Error',
    message: (() => {
      for (const key in err) {
        if (err[key]) {
          return typeof err[key] === 'object'
            ? JSON.stringify(err[key])
            : err[key];
        }
      }
      return err;
    })(),
    status: 'error',
  };

  if (ignoredRoutes.includes(router.currentRoute.value.matched[0]?.path)) {
    if (ignoredRoutes.includes(router.currentRoute.value.matched[0]?.path)) {
      datadogRum.addError(options.message, { category: 'reportsErrors' });
    }
    return;
  }

  if (options.message !== 'canceled' && err.status !== 401) {
    vm?.$toast?.show?.(options);
  }
};

window.addEventListener('unhandledrejection', (event) => {
  // A failed dynamic import after a deploy means a stale build is referencing
  // chunks that no longer exist. Reload to pick up the new build instead of
  // surfacing a dead-end error toast.
  if (isChunkLoadError(event.reason)) {
    event.preventDefault();
    reloadForNewChunks();
    return;
  }

  const message = (() => {
    const reason = event.reason;
    if (reason?.status === 404) {
      return reason.message || 'This resource was not found';
    }
    return reason?.message || reason || 'An unknown error occurred';
  })();

  // Check if this is an error from an unauthenticated state or navigation guard
  const isAuthenticated = store.getters['session/isAuthenticated'];
  const currentRoute = router.currentRoute.value;
  const isProtectedRoute = !currentRoute.matched.some(
    (route) => route.meta.allowAnonymous
  );

  // Ignore errors when:
  // 1. User is not authenticated trying to access protected routes
  // 2. Standard cancellation or 401 errors
  // 3. Errors on ignored routes (like reports)
  if (
    message === 'canceled' ||
    event.reason?.status === 401 ||
    (!isAuthenticated && isProtectedRoute) ||
    ignoredRoutes.includes(currentRoute.matched[0]?.path)
  ) {
    if (ignoredRoutes.includes(currentRoute.matched[0]?.path)) {
      datadogRum.addError(message, { category: 'unhandledReportsErrors' });
    }
    return;
  }

  app.config.globalProperties.$toast?.show?.({
    title: 'Error',
    message,
    status: 'error',
  });
});

router.isReady().then(() => {
  app.mount('#app');

  // Initialize global input trimming functionality
  initializeInputTrimming();

  if (window.location.search.includes('cacheBust')) {
    const url = window.location.origin + window.location.pathname;
    window.history.replaceState({}, document.title, url);
  }
});
